import { BoltRounded } from "@mui/icons-material";
import {
    Box,
    Chip,
    CircularProgress,
    Link,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxToggleButton from "components/UI/AcxToggleButton";
import React, { useCallback, useDeferredValue, useMemo, useState } from "react";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";
import {
    KboFlowDebugService,
    KboFlowStepDebugExecuteResult,
} from "services/KboFlowDebugService";
import { KboFlowStepDebugSkill } from "services/KboFlowStepDebugService";
import { FlowStep } from "./FlowStepCatalog";
import { SectionCard, SectionCardHeader } from "./FlowStepDebugUI";
import { countEditedLines } from "./lineDiff";
import {
    changedOutput,
    flattenOutput,
    hasFocusedOutputComparison,
    meaningfulOutputRows,
    OutputChange,
} from "./outputPaths";

/**
 * "Skill Instructions" stage: pick one of the step's skills and edit a copy of its instructions.
 * Edits stay in the browser — nothing here writes back to the skill.
 */

/** Which panel the instruction editor is showing. */
type EditorView = "edit" | "changes";

/**
 * Both editor panels are pinned to this height so switching between them, and typing inside the
 * editor, never resizes the card.
 */
const EDITOR_MIN_HEIGHT = 360;

/** Output tables scroll inside this rather than pushing the Evaluate footer off screen. */
const OUTPUT_TABLE_MAX_HEIGHT = 460;

/** Draft instruction text keyed by skill id; absent means "unedited". */
export type SkillDrafts = Record<string, string>;

function SkillBanner({
    skills,
    selectedSkillId,
    onSelectSkill,
    drafts,
}: {
    skills: KboFlowStepDebugSkill[];
    selectedSkillId?: string;
    onSelectSkill: (skillId: string) => void;
    drafts: SkillDrafts;
}) {
    const theme = useTheme();
    return (
        <Box
            sx={{
                alignItems: { xs: "flex-start", md: "center" },
                bgcolor: "primary.50",
                border: "1px solid",
                borderColor: theme.palette.gray[200],
                borderRadius: 2.5,
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                mb: 3,
                px: 2.5,
                py: 2,
            }}
        >
            <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                Skills in this step
            </Typography>
            {skills.map((skill) => {
                const isSelected = skill.id === selectedSkillId;
                const isEdited =
                    drafts[skill.id] !== undefined &&
                    drafts[skill.id] !== skill.skillText;
                return (
                    <Chip
                        key={skill.id}
                        clickable
                        label={skill.name ?? "Unnamed skill"}
                        onClick={() => onSelectSkill(skill.id)}
                        variant={isSelected ? "filled" : "outlined"}
                        color={isSelected ? "primary" : "default"}
                        sx={{
                            bgcolor: isSelected
                                ? theme.palette.common.white
                                : "transparent",
                            borderColor: isSelected
                                ? theme.palette.primary.main
                                : theme.palette.gray[300],
                            color: isSelected
                                ? theme.palette.primary.main
                                : theme.palette.gray[700],
                            fontWeight: 600,
                            ...(isEdited && {
                                "&::after": {
                                    bgcolor: "warning.main",
                                    borderRadius: "50%",
                                    content: '""',
                                    height: 6,
                                    ml: 0.75,
                                    width: 6,
                                },
                            }),
                        }}
                    />
                );
            })}
            <Box sx={{ flex: 1 }} />
            <Typography variant="body2" color="text.secondary">
                Select a skill to edit its instructions.
            </Typography>
        </Box>
    );
}

function InstructionEditor({
    skill,
    draft,
    onDraftChange,
    onResetToDefault,
}: {
    skill: KboFlowStepDebugSkill;
    draft: string;
    onDraftChange: (text: string) => void;
    onResetToDefault: () => void;
}) {
    const theme = useTheme();
    const [requestedView, setRequestedView] = useState<EditorView>("edit");
    const isEdited = draft !== skill.skillText;

    // With nothing edited there is no diff to show, so "changes" collapses back to the editor
    // rather than needing an effect to reset it.
    const view: EditorView = isEdited ? requestedView : "edit";

    // The edit count is an O(n*m) LCS over the whole skill. Deferring it keeps typing on the
    // fast path and lets React drop stale counts instead of computing one per keystroke.
    const countedDraft = useDeferredValue(draft);
    const editCount = useMemo(
        () => countEditedLines(skill.skillText, countedDraft),
        [skill.skillText, countedDraft],
    );

    return (
        <SectionCard sx={{ p: 2.5 }}>
            <Box sx={{ alignItems: "center", display: "flex", gap: 1.5 }}>
                <Typography
                    component="h2"
                    sx={{ fontSize: 18, fontWeight: 700, minWidth: 0 }}
                >
                    {skill.name ?? "Unnamed skill"}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {isEdited && (
                    <>
                        <Chip
                            size="small"
                            label="Edited"
                            color="warning"
                            sx={{ fontWeight: 700 }}
                        />
                        <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={onResetToDefault}
                            sx={{ fontWeight: 700 }}
                        >
                            Reset to default
                        </Link>
                    </>
                )}
            </Box>

            {skill.description && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                >
                    {skill.description}
                </Typography>
            )}

            <Box
                sx={{
                    border: "1px solid",
                    borderColor: theme.palette.gray[200],
                    borderRadius: 2,
                    mt: 2,
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        alignItems: "center",
                        borderBottom: "1px solid",
                        borderColor: theme.palette.gray[200],
                        display: "flex",
                        px: 2,
                        py: 1.25,
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Editing a copy
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <AcxToggleButton
                        id={`flow-step-debug-editor-view-${skill.id}`}
                        exclusive
                        defaultValue={view}
                        onChange={(value: EditorView | null) =>
                            value && setRequestedView(value)
                        }
                        items={[
                            { value: "edit", element: "Edit" },
                            {
                                value: "changes",
                                element: "Changes",
                                disabled: !isEdited,
                            },
                        ]}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            color: "primary.main",
                            fontWeight: 700,
                            ml: 1.5,
                        }}
                    >
                        {editCount} edit{editCount === 1 ? "" : "s"}
                    </Typography>
                </Box>

                {view === "edit" ? (
                    <Box
                        component="textarea"
                        value={draft}
                        onChange={(
                            event: React.ChangeEvent<HTMLTextAreaElement>,
                        ) => onDraftChange(event.target.value)}
                        aria-label={`${skill.name ?? "Skill"} instructions`}
                        sx={{
                            border: 0,
                            display: "block",
                            fontFamily: "monospace",
                            fontSize: 13,
                            lineHeight: 1.6,
                            minHeight: EDITOR_MIN_HEIGHT,
                            outline: "none",
                            p: 2,
                            resize: "vertical",
                            width: "100%",
                        }}
                    />
                ) : (
                    <Box
                        sx={{
                            fontSize: 13,
                            maxHeight: EDITOR_MIN_HEIGHT,
                            minHeight: EDITOR_MIN_HEIGHT,
                            overflow: "auto",
                        }}
                    >
                        <ReactDiffViewer
                            oldValue={skill.skillText}
                            newValue={draft}
                            splitView={false}
                            compareMethod={DiffMethod.WORDS}
                            showDiffOnly
                        />
                    </Box>
                )}
            </Box>
        </SectionCard>
    );
}

function MetricTile({ label, value, note }: { label: string; value: string; note?: string }) {
    const theme = useTheme();
    return (
        <Box sx={{ px: 2.5, py: 2 }}>
            <Typography
                sx={{
                    color: theme.palette.gray[500],
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontFamily: "monospace",
                    fontSize: 24,
                    fontWeight: 700,
                    mt: 0.5,
                }}
            >
                {value}
            </Typography>
            {note && (
                <Typography variant="caption" color="text.secondary">
                    {note}
                </Typography>
            )}
        </Box>
    );
}

function OutputValue({
    value,
    missingLabel,
}: {
    value?: string;
    missingLabel: string;
}) {
    const theme = useTheme();
    if (value !== undefined && value !== "") return value;

    return (
        <Typography
            component="span"
            sx={{ color: theme.palette.gray[500], fontSize: 13, fontStyle: "italic" }}
        >
            {value === "" ? "Empty string" : missingLabel}
        </Typography>
    );
}

/** Only the output paths changed by the test, paired with their before and after values. */
function OutputChangesTable({
    step,
    sourceRunVersion,
    changes,
    focused,
}: {
    step: FlowStep;
    sourceRunVersion: number;
    changes: OutputChange[];
    focused: boolean;
}) {
    const theme = useTheme();

    return (
        <SectionCard>
            <SectionCardHeader
                title={
                    <>
                        Changed output{" "}
                        <Box
                            component="span"
                            sx={{
                                color: theme.palette.gray[500],
                                fontFamily: "monospace",
                                fontSize: 13,
                                fontWeight: 500,
                            }}
                        >
                            {step.activityName}
                        </Box>
                    </>
                }
                subtitle={
                    changes.length === 0
                        ? `No ${focused ? "relevant " : ""}values changed from original run v${sourceRunVersion}.`
                        : `${changes.length} ${focused ? "relevant " : ""}value${changes.length === 1 ? "" : "s"} changed from original run v${sourceRunVersion}.${focused ? " IDs, raw evidence, and execution details are excluded." : ""}`
                }
            />
            {changes.length === 0 ? (
                <Box sx={{ p: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                        {focused
                            ? "This test produced the same meaningful output as the original run."
                            : "This test produced the same output as the original run."}
                    </Typography>
                </Box>
            ) : (
                <TableContainer sx={{ maxHeight: OUTPUT_TABLE_MAX_HEIGHT }}>
                    <Table
                        stickyHeader
                        size="small"
                        aria-label="Changed output"
                        sx={{ "& thead th": { bgcolor: theme.palette.common.white } }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: "30%" }}>
                                    Output path
                                </TableCell>
                                <TableCell sx={{ width: "35%" }}>
                                    Before
                                </TableCell>
                                <TableCell sx={{ width: "35%" }}>After</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {changes.map((change) => (
                                <TableRow key={change.path}>
                                    <TableCell
                                        sx={{
                                            color: theme.palette.gray[600],
                                            fontFamily: "monospace",
                                            fontSize: 12.5,
                                            verticalAlign: "top",
                                            wordBreak: "break-all",
                                        }}
                                    >
                                        {change.path || "(root)"}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontSize: 13.5,
                                            verticalAlign: "top",
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        <OutputValue
                                            value={change.before}
                                            missingLabel="Not present"
                                        />
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontSize: 13.5,
                                            verticalAlign: "top",
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        <OutputValue
                                            value={change.after}
                                            missingLabel="Removed"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </SectionCard>
    );
}

/**
 * Right-hand results column: what the step produced on this run. There is no stored copy — this is
 * the only one, and it is gone when the page is left.
 */
function StepResults({
    step,
    running,
    result,
    disabledReason,
    onEvaluate,
}: {
    step: FlowStep;
    running: boolean;
    result?: KboFlowStepDebugExecuteResult;
    /** Why Run test is unavailable, when it is. */
    disabledReason?: string;
    onEvaluate: () => void;
}) {
    const theme = useTheme();

    const produced = useMemo(
        () => flattenOutput(result?.output),
        [result?.output],
    );
    const original = useMemo(
        () => flattenOutput(result?.originalOutput),
        [result?.originalOutput],
    );
    const focused = hasFocusedOutputComparison(step.id);
    const meaningfulProduced = useMemo(
        () => meaningfulOutputRows(step.id, produced),
        [step.id, produced],
    );
    const meaningfulOriginal = useMemo(
        () => meaningfulOutputRows(step.id, original),
        [step.id, original],
    );
    const changes = useMemo(
        () => changedOutput(meaningfulProduced, meaningfulOriginal),
        [meaningfulProduced, meaningfulOriginal],
    );

    if (running) {
        return (
            <SectionCard>
                <SectionCardHeader
                    title="Running"
                    subtitle={`${step.label} is executing against the selected run.`}
                />
                <Box sx={{ p: 6, textAlign: "center" }}>
                    <CircularProgress size={28} />
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 2 }}
                    >
                        This is a live model call and can take a few minutes.
                    </Typography>
                </Box>
            </SectionCard>
        );
    }

    if (!result) {
        return (
            <SectionCard>
                <SectionCardHeader
                    title="Step output"
                    subtitle={`What ${step.label} produces when you run it.`}
                />
                <Box sx={{ p: 6, textAlign: "center" }}>
                    <Typography sx={{ fontWeight: 700 }}>
                        Nothing run yet
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        {disabledReason ??
                            "Edit the instructions on the left, then run the step to see what changed."}
                    </Typography>
                </Box>
            </SectionCard>
        );
    }

    const { metrics } = result;
    const totalTokens = metrics.inputTokens + metrics.outputTokens;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <SectionCard>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(3, 1fr)",
                        },
                        "& > *:not(:first-of-type)": {
                            borderLeft: { sm: "1px solid" },
                            borderTop: { xs: "1px solid", sm: "none" },
                            borderColor: {
                                xs: theme.palette.gray[200],
                                sm: theme.palette.gray[200],
                            },
                        },
                    }}
                >
                    <MetricTile
                        label="Time"
                        value={`${(metrics.elapsedMs / 1000).toFixed(1)}s`}
                        note={metrics.model ?? undefined}
                    />
                    <MetricTile
                        label="Tokens"
                        value={totalTokens.toLocaleString()}
                        note={`in ${metrics.inputTokens.toLocaleString()} · cached ${metrics.cachedInputTokens.toLocaleString()} · out ${metrics.outputTokens.toLocaleString()}`}
                    />
                    <MetricTile
                        label="Invocations"
                        value={String(metrics.invocations)}
                        note={
                            metrics.invocations === 1
                                ? "one model call"
                                : "model calls recorded"
                        }
                    />
                </Box>
            </SectionCard>

            {result.success ? (
                <>
                    <OutputChangesTable
                        step={step}
                        sourceRunVersion={result.sourceRunVersion}
                        changes={changes}
                        focused={focused}
                    />

                    <SectionCard>
                        <Box
                            sx={{
                                alignItems: { xs: "stretch", md: "center" },
                                display: "flex",
                                flexDirection: { xs: "column", md: "row" },
                                gap: 2,
                                px: 2.5,
                                py: 2,
                            }}
                        >
                            <BoltRounded
                                sx={{ color: "primary.main" }}
                                fontSize="small"
                            />
                            <Typography variant="body2" color="text.secondary">
                                Done editing?{" "}
                                <Box
                                    component="span"
                                    sx={{ color: "text.primary", fontWeight: 700 }}
                                >
                                    Evaluate outputs
                                </Box>{" "}
                                summarizes every change against the original run
                                before you promote a version.
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            <AcxButton color="primary" onClick={onEvaluate}>
                                Evaluate outputs
                            </AcxButton>
                        </Box>
                    </SectionCard>
                </>
            ) : (
                <SectionCard>
                    <SectionCardHeader title="Step failed" subtitle={step.label} />
                    <Box sx={{ p: 2.5 }}>
                        <Typography color="error" sx={{ fontWeight: 600 }}>
                            {result.error ?? "The step failed."}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            Nothing was written — the original run is untouched.
                        </Typography>
                    </Box>
                </SectionCard>
            )}
        </Box>
    );
}

export function FlowStepDebugSkills({
    step,
    skills,
    loading,
    error,
    selectedSkillId,
    onSelectSkill,
    drafts,
    onDraftsChange,
    service,
    sourceRunId,
    nodeId,
    onEvaluate,
    result,
    onResultChange,
}: {
    step: FlowStep;
    skills: KboFlowStepDebugSkill[];
    loading: boolean;
    error?: string;
    selectedSkillId?: string;
    onSelectSkill: (skillId: string) => void;
    drafts: SkillDrafts;
    onDraftsChange: (drafts: SkillDrafts) => void;
    service: KboFlowDebugService;
    /** The run the step replays. Absent when the configure stage chose manual input. */
    sourceRunId?: string;
    /** Exploration node to analyze; only meaningful for "analyze-one-node". */
    nodeId?: string;
    /** Advances the wizard once the caller is done comparing outputs. */
    onEvaluate: () => void;
    /** Owned by the page so the Evaluation stage can judge the same result. */
    result?: KboFlowStepDebugExecuteResult;
    onResultChange: (result: KboFlowStepDebugExecuteResult | undefined) => void;
}) {
    const [running, setRunning] = useState(false);
    const selectedSkill = skills.find((skill) => skill.id === selectedSkillId);
    const draft = selectedSkill
        ? (drafts[selectedSkill.id] ?? selectedSkill.skillText)
        : "";

    const setDraft = (text: string) => {
        if (!selectedSkill) return;
        onDraftsChange({ ...drafts, [selectedSkill.id]: text });
    };

    const discardEdits = () => {
        if (!selectedSkill) return;
        const next = { ...drafts };
        delete next[selectedSkill.id];
        onDraftsChange(next);
    };

    // Only the edits that actually differ are sent; an untouched skill resolves normally on the
    // OrchestrationEngine side.
    const runStep = useCallback(async () => {
        if (!sourceRunId) return;
        setRunning(true);
        onResultChange(undefined);
        try {
            onResultChange(
                await service.runStepDebug({
                    sourceRunId,
                    stepName: step.id,
                    nodeId,
                    skillOverrides: skills
                        .filter(
                            (skill) =>
                                drafts[skill.id] !== undefined &&
                                drafts[skill.id] !== skill.skillText,
                        )
                        .map((skill) => ({
                            name: skill.name ?? "",
                            skillText: drafts[skill.id],
                        })),
                }),
            );
        } catch (caught: unknown) {
            onResultChange({
                success: false,
                error:
                    caught instanceof Error
                        ? caught.message
                        : "The step could not be run.",
                sourceRunVersion: 0,
                metrics: {
                    elapsedMs: 0,
                    invocations: 0,
                    inputTokens: 0,
                    outputTokens: 0,
                    cachedInputTokens: 0,
                },
            });
        } finally {
            setRunning(false);
        }
    }, [service, sourceRunId, nodeId, step.id, skills, drafts, onResultChange]);

    const disabledReason = !sourceRunId
        ? "Running a step replays a completed run, so pick one on the Configure tab first."
        : undefined;

    if (loading) {
        return (
            <>
                <Skeleton height={72} sx={{ mb: 3 }} variant="rounded" />
                <Skeleton height={420} variant="rounded" />
            </>
        );
    }

    if (error) {
        return (
            <SectionCard>
                <Box sx={{ p: 3 }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            </SectionCard>
        );
    }

    // A step with no skills is still runnable — there is just nothing to edit. It gets the results
    // column and a Run test, so every step in the flow can be executed and compared.
    if (step.skillNames.length === 0) {
        return (
            <Box
                sx={{
                    alignItems: "flex-start",
                    display: "grid",
                    gap: 3,
                    gridTemplateColumns: {
                        xs: "1fr",
                        md: "minmax(320px, 1fr) minmax(0, 1.6fr)",
                    },
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <SectionCard sx={{ p: 2.5 }}>
                        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
                            {step.label}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                        >
                            {step.activityName} runs no agent that loads a skill,
                            so there are no instructions to edit. Run it to see what
                            it produces against the original run.
                        </Typography>
                    </SectionCard>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <AcxButton
                            color="primary"
                            loading={running}
                            disabled={running || !sourceRunId}
                            title={disabledReason}
                            onClick={runStep}
                        >
                            Run test
                        </AcxButton>
                    </Box>
                </Box>
                <StepResults
                    step={step}
                    running={running}
                    result={result}
                    disabledReason={disabledReason}
                    onEvaluate={onEvaluate}
                />
            </Box>
        );
    }

    if (skills.length === 0) {
        return (
            <SectionCard>
                <Box sx={{ p: 6, textAlign: "center" }}>
                    <Typography sx={{ fontWeight: 700 }}>
                        Skills for this step were not found
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        No active KboFlow-scoped skill in the core database is
                        named {step.skillNames.join(", ")}.
                    </Typography>
                </Box>
            </SectionCard>
        );
    }

    return (
        <>
            <SkillBanner
                skills={skills}
                selectedSkillId={selectedSkillId}
                onSelectSkill={onSelectSkill}
                drafts={drafts}
            />

            <Box
                sx={{
                    alignItems: "flex-start",
                    display: "grid",
                    gap: 3,
                    gridTemplateColumns: {
                        xs: "1fr",
                        md: "minmax(320px, 1fr) minmax(0, 1.6fr)",
                    },
                }}
            >
                <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                >
                    {selectedSkill && (
                        <>
                            <InstructionEditor
                                skill={selectedSkill}
                                draft={draft}
                                onDraftChange={setDraft}
                                onResetToDefault={discardEdits}
                            />
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    justifyContent: "flex-end",
                                }}
                            >
                                <AcxButton
                                    color="white"
                                    variant="outlined"
                                    disabled={
                                        running ||
                                        draft === selectedSkill.skillText
                                    }
                                    onClick={discardEdits}
                                >
                                    Discard edits
                                </AcxButton>
                                <AcxButton
                                    color="primary"
                                    loading={running}
                                    disabled={running || !sourceRunId}
                                    title={disabledReason}
                                    onClick={runStep}
                                >
                                    Run test
                                </AcxButton>
                            </Box>
                        </>
                    )}
                </Box>

                <StepResults
                    step={step}
                    running={running}
                    result={result}
                    disabledReason={disabledReason}
                    onEvaluate={onEvaluate}
                />
            </Box>
        </>
    );
}
